<template>
  <div class="patientServiceWrapper">
    <!-- 患者档案 -->
    <div class="patientRecordWrapper">
      <PatientRecord :patient="patientInfo" />
    </div>
    <!-- 服务统计 -->
    <div class="serviceStatsWrapper">
      <ServiceStats :list="serviceStatsList" :data="serviceData" />
    </div>
    <!-- 服务订单 -->
    <div class="serviceOrderWrapper">
      <div class="header">
        <Header title="服务订单" />
      </div>
      <a-spin :spinning="orderData.loading" wrapperClassName="orderContentWrapper">
        <div class="orderContent">
          <template v-if="orderData.list.length > 0">
            <div ref="orderListRef" class="orderListWrapper" @scroll="updateShadow">
              <div
                v-for="(order, index) in orderData.list"
                :key="index"
                class="orderItem"
                @click="goServiceDetail(order)"
              >
                <OrderCard :data="order" :orderDiagnosisList="orderDiagnosisList" />
              </div>
              <!-- 阴影吸附在底部 -->
              <div v-show="showShadow" class="timeline-shadow"></div>
            </div>
            <a-pagination
              :pageSizeOptions="['10', '20', '30', '40']"
              show-size-changer
              show-quick-jumper
              v-model:current="pagination.curPage"
              v-model:pageSize="pagination.pageSize"
              :total="pagination.total"
              @change="onShowSizeChange"
              @showSizeChange="onShowSizeChange"
              :show-total="total => `共 ${pagination.total} 条`"
            />
          </template>
          <div class="emptyWrapper" v-else>
            <Empty :emptyImg="emptyImg" emptyText="暂无搜索结果" />
          </div>
        </div>
      </a-spin>
    </div>
    <!-- 底部footer -->
    <div class="footerWrapper">
      <BottomCopy />
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, defineProps, onMounted, markRaw, defineEmits, nextTick } from 'vue'
import PatientRecord from '../components/PatientRecord'
import ServiceStats from '../components/ServiceStats'
import {
  getPatientInfo,
  getServiceSatics,
  getServiceOrderList
} from '@/api/workPlace/serviceAttribution'
import { serviceStatsList } from '../constant'
import emptyImg from '@/assets/img/empty.png'
import Empty from '../components/Empty'
import OrderCard from './components/OrderCard'
import Header from '../components/Header'
import BottomCopy from '../components/BottomCopy'
import { orderDiagnosisList } from './constant'
import ServiceDetail from '../ServiceDetail'
const emit = defineEmits(['pushBreadcrumb'])
const props = defineProps({
  ucDoctorId: {
    type: String,
    default: ''
  },
  userId: {
    type: String,
    default: ''
  },
  ucPatientId: {
    type: Number,
    default: null
  }
})
const orderListRef = ref(null)
// 展示底部阴影
const showShadow = ref(true)
// 服务统计数据
const serviceData = reactive({})
// 服务患者信息
const patientInfo = reactive({})
// 服务订单数据
const orderData = reactive({
  list: [],
  loading: false
})
const initPagination = {
  curPage: 1,
  pageSize: 10,
  showQuickJumper: true,
  showSizeChanger: true,
  total: 0,
  showTotal: () => {}
}
const pagination = reactive({ ...initPagination })
// 获取服务患者信息
const fetchPatientInfo = async () => {
  const { ucPatientId } = props
  const ucUserId = ucPatientId
  const result = await getPatientInfo(ucUserId)
  Object.assign(patientInfo, {
    ...result
  })
}
// 获取服务统计信息
const fetchServiceSatics = async () => {
  const { ucDoctorId, ucPatientId } = props
  const params = {
    ucDoctorId,
    ucPatientId
  }
  const result = await getServiceSatics(params)
  Object.assign(serviceData, result)
}
// 改变页数条数
const onShowSizeChange = (page, pageSize) => {
  pagination.curPage = page
  pagination.pageSize = pageSize
  fetchOrderListData()
}
/**
 * 处理时间线底部阴影的显示逻辑
 * 1. 等待 DOM 更新完成（nextTick）
 * 2. 将列表滚动条重置到顶部（避免保留旧滚动位置）
 * 3. 重新计算阴影是否需要显示（updateShadow）
 */
const handleShadowVisible = () => {
  nextTick(() => {
    if (orderListRef.value) {
      orderListRef.value.scrollTop = 0
    }
    updateShadow()
  })
}
// 更新服务订单数据
const updateOrderData = data => {
  orderData.list = data.dataList
  Object.assign(pagination, {
    total: data.totalSize
  })
  handleShadowVisible()
}
const handleError = () => {
  orderData.list = []
}
// 获取服务订单数据
const fetchServicePatientDetails = async () => {
  const { curPage, pageSize } = pagination
  const { ucDoctorId, ucPatientId } = props
  const params = {
    curPage,
    pageSize,
    ucDoctorId,
    ucPatientId
  }
  const result = await getServiceOrderList(params)
  return result
}
// 获取服务订单列表
const fetchOrderListData = async () => {
  try {
    orderData.loading = true
    const result = await fetchServicePatientDetails()
    updateOrderData(result)
  } catch (error) {
    handleError(error)
  } finally {
    orderData.loading = false
  }
}
// 跳转至服务详情
const goServiceDetail = order => {
  const { ucPatientId, userId } = props
  const breadcrumbStack = {
    title: '服务详情',
    component: markRaw(ServiceDetail),
    props: {
      ucPatientId,
      userId,
      parentInstanceSn: order.recomParentSn
    }
  }
  emit('pushBreadcrumb', breadcrumbStack)
}
// 监听服务患者滚动
const updateShadow = () => {
  if (!orderListRef.value) return
  const bottomDistance =
    orderListRef.value.scrollHeight - orderListRef.value.scrollTop - orderListRef.value.clientHeight
  showShadow.value = bottomDistance < 10 ? false : true
}
// 初始化数据
onMounted(() => {
  // 获取服务患者信息
  fetchPatientInfo()
  // 获取服务统计信息
  fetchServiceSatics()
  // 获取服务订单列表
  fetchOrderListData()
})
</script>

<style scoped lang="scss">
.patientServiceWrapper {
  width: 100%;
  height: 100%;
  padding-bottom: 16px;
  display: flex;
  flex-direction: column;
  .patientRecordWrapper {
    margin-bottom: 16px;
    height: 64px;
  }

  .serviceStatsWrapper {
    padding: 0 24px;
  }

  .serviceOrderWrapper {
    flex: 1;
    padding: 0px 24px;
    display: flex;
    flex-direction: column;
    min-height: 0;

    .header {
      padding-bottom: 16px;
      margin-bottom: 16px;
      border-bottom: 1px solid #eff4f9;
    }
    .orderContentWrapper {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-height: 0;

      ::v-deep(.ant-spin-container) {
        flex: 1;
        display: flex;
        flex-direction: column;
        min-height: 0;
      }

      .orderContent {
        flex: 1;
        display: flex;
        flex-direction: column;
        min-height: 0;

        .orderListWrapper {
          flex: 1;
          overflow: auto;
          display: flex;
          flex-wrap: wrap;
          align-items: flex-start;
          align-content: flex-start;
          margin-bottom: 16px;
          position: relative;

          .orderItem {
            width: 25%;
            min-width: 300px;
          }

          .timeline-shadow {
            position: sticky;
            bottom: 0;
            width: 100%;
            height: 8px;
            pointer-events: none;
            background: linear-gradient(to top, rgba(0, 0, 0, 0.05), transparent);
          }
        }

        .emptyWrapper {
          width: 100%;
          height: 80%;
        }
      }
    }
  }

  .footerWrapper {
    margin-top: 12px;
  }
}
</style>
